import { useLoaderData } from '@remix-run/react';

import { type GenericConfig } from '~/types';
import { type LandingPageSectionName, type SectionMap } from '~/types/template';

import { type loader } from '../../loader.server';
import { renderComponents } from '../../utils/renderComponents';
import { LandingPageHero } from '../LandingPageHero/LandingPageHero';
import { Section } from '../Section/Section';

import styles from './LandingPageTemplate.module.css';

type LandingPageTemplateProps = {
  backgroundImage?: string;
  ctaSubText?: string[];
  ctaText?: string;
  ctaUrl?: string;
  genericConfig: GenericConfig;
  h1Heading?: string;
  introText?: string;
  sections: Pick<SectionMap, LandingPageSectionName>;
};

export const LandingPageTemplate = ({
  backgroundImage,
  ctaSubText,
  ctaText,
  ctaUrl,
  genericConfig,
  h1Heading,
  introText,
  sections,
}: LandingPageTemplateProps) => {
  const { currentPathWithRepub } = useLoaderData<typeof loader>();

  return (
    <div
      className={styles.outerWrapper}
      style={backgroundImage ? ({ '--bg-image': `url(${backgroundImage})` } as React.CSSProperties) : undefined}
    >
      <div className={styles.innerContainer}>
        <Section
          ariaLabel="Landing Page Header"
          className={styles.top}
          content={renderComponents({ components: sections.top ?? null, genericConfig })}
          dataTestId="landing-page-top"
          id="landing-page-top"
          show
        />
        {h1Heading && ctaText && ctaUrl && introText && (
          <LandingPageHero
            ctaSubText={ctaSubText}
            ctaText={ctaText}
            ctaUrl={ctaUrl}
            currentPathWithRepub={currentPathWithRepub}
            h1Heading={h1Heading}
            introText={introText}
          />
        )}
        <div className={styles.centerGrid}>
          <Section
            ariaLabel="Landing Page Left Section"
            className={styles.centerLeft}
            content={renderComponents({ components: sections.centerLeft ?? null, genericConfig })}
            dataTestId="landing-page-center-left"
            id="landing-page-center-left"
            show
          />
          <Section
            ariaLabel="Landing Page Right Section"
            className={styles.centerRight}
            content={renderComponents({ components: sections.centerRight ?? null, genericConfig })}
            dataTestId="landing-page-center-right"
            id="landing-page-center-right"
            show
          />
        </div>
        <Section
          ariaLabel="Landing Page Center Bottom"
          className={styles.centerBottom}
          content={renderComponents({ components: sections.centerBottom ?? null, genericConfig })}
          dataTestId="landing-page-center-bottom"
          id="landing-page-center-bottom"
          show
        />
      </div>
      <Section
        ariaLabel="Landing Page Footer"
        className={styles.bottom}
        content={renderComponents({ components: sections.bottom ?? null, genericConfig })}
        dataTestId="landing-page-bottom"
        id="landing-page-bottom"
        show
      />
    </div>
  );
};
